import { Injectable } from '@angular/core'
import { ReplaySubject } from 'rxjs'
import { IdbService } from './idb.service.js'
import { StudioTrpcService } from './studio-trpc.service.js'
import color from 'color'
import { addSeconds } from 'date-fns'
import { EnvironmentSettings, idbSettingName } from './environment-settings.js'
import { EnvironmentSettings as EnvSettings } from '@lg/shared'

const cacheValiditySeconds = 300

@Injectable({
	providedIn: 'root',
})
export class EnvironmentService {
	private environmentSubject = new ReplaySubject<EnvironmentSettings>()
	environment$ = this.environmentSubject.asObservable()

	constructor(
		private trpcService: StudioTrpcService,
		private idbService: IdbService,
	) {
		this.init()
	}

	async init() {
		await this.updateEnvironment()
	}

	async updateEnvironment() {
		let environment: EnvironmentSettings | undefined =
			await this.idbService.get(idbSettingName)
		if (environment) {
			this.environmentSubject.next(environment)

			const cacheValidForMs =
				(environment.cacheUntil ?? new Date()).getTime() - new Date().getTime()
			if (cacheValidForMs > 0) {
				setTimeout(() => this.updateEnvironment(), cacheValidForMs)
				return
			}
		}

		const result = (await this.trpcService.client.settings.getPublic.query({
			id: 'Environment',
			properties: ['publicName', 'shortName', 'color'],
		})) as EnvSettings

		if (!result.shortName)
			result.shortName = '[Setting System.Environment.shortName is not defined]'

		environment = {
			publicName: result.publicName,
			shortName: result.shortName,
			bannerText: new Array(Math.ceil(500 / result.shortName.length))
				.fill(result.shortName)
				.join(' — '),
			bgColor: result.color,
			fgColor: color(result.color).isLight() ? 'black' : 'white',
			cacheUntil: addSeconds(new Date(), cacheValiditySeconds),
		}
		this.environmentSubject.next(environment)
		await this.idbService.put(idbSettingName, environment)
		setTimeout(() => this.updateEnvironment(), cacheValiditySeconds * 1000)
	}
}
